import type { ReactNode } from 'react';
import { Header, type EyebrowTone, type TitleAccent } from './_shared';

/** A header column: a plain title, or a rich header with a tag + CTA. */
export type TableColumn =
  | string
  | { text: string; tag?: string; cta?: string };

/**
 * A body cell. Strings render as text; `'yes'`/`'no'` render the positive /
 * negative mark; objects render rich content (text + remark, text + CTA, or a tag).
 */
export type TableCell =
  | string
  | 'yes'
  | 'no'
  | { text: string; remark?: string }
  | { text: string; cta: string }
  | { tag: string };

export type TableRow = {
  label: string;
  cells: readonly TableCell[];
};

export type TableSlideProps = {
  eyebrow: ReactNode;
  eyebrowTone?: EyebrowTone;
  title: ReactNode;
  titleAccent?: TitleAccent;
  sub?: ReactNode;
  columns: readonly TableColumn[];
  rows: readonly TableRow[];
  legend?: { yes: string; no: string };
  /** 'logo' = SN logomark / person glyph (default). 'check' = blue tick / em-dash. */
  mark?: 'logo' | 'check';
  /** 0-based column index to highlight with a tinted band. */
  highlightColumn?: number;
  /** Header surface — dark teal (default) or light (Teal-01). */
  headerStyle?: 'light' | 'dark';
  /** Alignment for text / CTA value cells. Marks are always centered. */
  align?: 'left' | 'center';
};

function Chevron({ dir }: { dir: 'left' | 'right' }) {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d={dir === 'left' ? 'M10 4 6 8l4 4' : 'M6 4l4 4-4 4'} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** DS Tag — mono pill. tone: 'dark' (Teal-05 / white) | 'blue' (Blue-03 / Teal-06). */
function Tag({ label, tone = 'dark' }: { label: string; tone?: 'dark' | 'blue' }) {
  return <span className={`sn-table-tag sn-table-tag--${tone}`}>{label}</span>;
}

/** DS tertiary CTA — mono text bracketed by chevrons. Inherits heading color. */
function Cta({ label }: { label: string }) {
  return (
    <span className="sn-table-cta">
      <Chevron dir="left" />
      <span>{label}</span>
      <Chevron dir="right" />
    </span>
  );
}

function YesMark({ kind }: { kind: 'logo' | 'check' }) {
  if (kind === 'check') {
    return (
      <span className="sn-table-tick" aria-label="Yes">
        <svg viewBox="0 0 24 24" width="22" height="22">
          <circle cx="12" cy="12" r="11" fill="var(--sn-blue-5)" />
          <path d="M7 12.5l3.2 3.2L17 9" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    );
  }
  return <img className="sn-table-logo" src="/assets/logomark-blue.svg" alt="StreamNative" />;
}

function NoMark({ kind }: { kind: 'logo' | 'check' }) {
  if (kind === 'check') return <span className="sn-table-dash" aria-label="No">—</span>;
  return (
    <span className="sn-table-person" aria-label="Customer">
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="var(--sn-grey-5)" strokeWidth="1.8">
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
      </svg>
    </span>
  );
}

/** True when a cell renders a centered mark (always centered, ignores `align`). */
function isMarkCell(cell: TableCell): boolean {
  return cell === 'yes' || cell === 'no' || (typeof cell === 'object' && 'tag' in cell);
}

function Cell({ cell, kind }: { cell: TableCell; kind: 'logo' | 'check' }) {
  if (cell === 'yes') return <YesMark kind={kind} />;
  if (cell === 'no') return <NoMark kind={kind} />;
  if (typeof cell === 'string') return <span className="sn-table-text">{cell}</span>;
  if ('tag' in cell) return <Tag label={cell.tag} tone="dark" />;
  if ('cta' in cell) {
    return (
      <div className="sn-table-cellbox">
        <span className="sn-table-text">{cell.text}</span>
        <Cta label={cell.cta} />
      </div>
    );
  }
  return (
    <div className="sn-table-cellbox">
      <span className="sn-table-text">{cell.text}</span>
      {cell.remark && <span className="sn-table-remark">{cell.remark}</span>}
    </div>
  );
}

function HeaderCell({ col, headerStyle }: { col: TableColumn; headerStyle: 'light' | 'dark' }) {
  if (typeof col === 'string') return <>{col}</>;
  return (
    <div className="sn-th-rich">
      {col.tag && <Tag label={col.tag} tone={headerStyle === 'light' ? 'dark' : 'blue'} />}
      <span className="sn-th-text">{col.text}</span>
      {col.cta && <Cta label={col.cta} />}
    </div>
  );
}

export function TableSlide({
  eyebrow,
  eyebrowTone,
  title,
  titleAccent,
  sub,
  columns,
  rows,
  legend,
  mark = 'logo',
  highlightColumn,
  headerStyle = 'dark',
  align = 'center',
}: TableSlideProps) {
  const hl = (i: number) => (i === highlightColumn ? ' sn-table-col--hl' : '');
  const tableClass = [
    'sn-table',
    `sn-table--${align}`,
    headerStyle === 'light' ? 'sn-table--header-light' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div className="slide-body">
      <Header eyebrow={eyebrow} eyebrowTone={eyebrowTone} title={title} titleAccent={titleAccent} sub={sub} />
      <div className="sn-work">
        {legend && (
          <div className="sn-table-legend">
            <span><YesMark kind={mark} /> {legend.yes}</span>
            <span><NoMark kind={mark} /> {legend.no}</span>
          </div>
        )}
        <table className={tableClass}>
          <thead>
            <tr>
              <th />
              {columns.map((c, i) => (
                <th key={i} className={`sn-th${hl(i)}`}>
                  <HeaderCell col={c} headerStyle={headerStyle} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label}>
                <th scope="row">{r.label}</th>
                {r.cells.map((cell, i) => (
                  <td key={i} className={`${hl(i)}${isMarkCell(cell) ? ' is-mark' : ''}`.trim()}>
                    <Cell cell={cell} kind={mark} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
